import { useCallback, useEffect, useRef } from 'react';
import { ComponentId } from '../../types/quoter/notifications';
import { useVersionState } from './VersionContext';
import logger from '../../utils/logging/logger';
const componentName = 'useComponentVersion';

// Use the same namespace as VersionContext
const VERSION_NAMESPACE = '__POINTSAILS_VERSION_SYSTEM__';

// Add at the top of both VersionContext.tsx and useComponentVersion.ts
logger.log('LogVersionUpdates', componentName, 'VERSION_NAMESPACE value:', VERSION_NAMESPACE);

interface UseComponentVersionOptions {
    onVersionChange?: (newVersion: number) => Promise<void> | void;
}

function debugGlobalState(label: string) {
    logger.log('LogVersionUpdates', componentName, `GLOBAL STATE DEBUG [${label}]:`, window[VERSION_NAMESPACE] ?
            { ...window[VERSION_NAMESPACE].componentVersions } : 'undefined',
        window[VERSION_NAMESPACE]?.masterVersion);
}

// Get version directly from global state for maximum reliability
function getVersionDirect(componentId: ComponentId): number {
    // Verify window exists
    if (typeof window === 'undefined') {
        logger.log('LogVersionUpdates', componentName, `!!!!!WARNING - window is undefined when getting version for ${componentId}`);
        return -1;
    }

    // Verify namespace exists - if not, initialize it
    if (!window[VERSION_NAMESPACE]) {
        logger.log('LogVersionUpdates', componentName, `!!!!!WARNING - VERSION_NAMESPACE not found when getting version for ${componentId}, initializing`);
        window[VERSION_NAMESPACE] = {
            masterVersion: 0,
            componentVersions: {},
            initialized: true
        };
        return -1;
    }

    // Verify componentVersions object exists
    if (!window[VERSION_NAMESPACE].componentVersions) {
        logger.log('LogVersionUpdates', componentName, `!!!!!WARNING - componentVersions object missing for ${componentId}, recreating`);
        window[VERSION_NAMESPACE].componentVersions = {};
        return -1;
    }

    // Get the version with more verbose logging
    const version = window[VERSION_NAMESPACE].componentVersions[componentId];
    if (version === undefined) {
        // Only log this if we expect the component to have a version already
        return -1;
    }

    return version;
}

// Set version directly in global state for maximum reliability
function setVersionDirect(componentId: ComponentId, version: number): void {
    // Verify window exists
    if (typeof window === 'undefined') {
        logger.log('LogIssue', componentName, `!!!!!WARNING - window is undefined when setting version for ${componentId}`);
        return;
    }

    // Make sure namespace exists - if not, create it
    if (!window[VERSION_NAMESPACE]) {
        logger.log('LogIssue', componentName, `!!!!!WARNING - VERSION_NAMESPACE not found when setting version for ${componentId}, initializing`);
        window[VERSION_NAMESPACE] = {
            masterVersion: 0,
            componentVersions: {},
            initialized: true
        };
    }

    // Make sure componentVersions object exists
    if (!window[VERSION_NAMESPACE].componentVersions) {
        logger.log('LogIssue', componentName, `!!!!!WARNING - componentVersions object missing for ${componentId}, recreating`);
        window[VERSION_NAMESPACE].componentVersions = {};
    }

    // Set the version
    window[VERSION_NAMESPACE].componentVersions[componentId] = version;

    // Verify the version was set correctly
    const verifyVersion = window[VERSION_NAMESPACE].componentVersions[componentId];
    if (verifyVersion !== version) {
        logger.log('LogIssue', componentName, `!!!!!ERROR - Failed to set version for ${componentId}: expected ${version}, got ${verifyVersion}`);
    }
}

// Get master version directly from global state
function getMasterVersionDirect(): number {
    if (typeof window === 'undefined' || !window[VERSION_NAMESPACE]) {
        return 0;
    }
    return window[VERSION_NAMESPACE].masterVersion;
}

// In hooks_quoter_useComponentVersion.ts
export const useComponentVersion = (
    componentId: ComponentId,
    options: UseComponentVersionOptions = {}
) => {

    // Inside useComponentVersion, early in the function
    debugGlobalState('Hook initialization');

    // Get context functions
    const {
        masterVersion,
        getComponentVersion,
        setComponentVersion,
        updateMasterVersion
    } = useVersionState();

    // When this changes it triggers the useEffect at the bottom of this file
    const { onVersionChange } = options;

    // Create refs to track the latest callbacks and values
    // This prevents stale closures in callbacks
    const onVersionChangeRef = useRef(onVersionChange);
    const masterVersionRef = useRef(masterVersion);
    const initialVersionCheckedRef = useRef(false);

    // Get the component's current version with direct global access for reliability
    const getCurrentVersion = useCallback(() => {
        // Try direct global access first with better debug logging
        const directVersion = getVersionDirect(componentId);

        // Only fall back to context if direct access failed with -1
        if (directVersion !== -1) {
            return directVersion;
        }

        // Before falling back, log details about global state
        debugGlobalState(`getCurrentVersion fallback for ${componentId}`);

        // Get context version
        const contextVersion = getComponentVersion(componentId);

        // Log the difference
        if (contextVersion !== -1 && directVersion === -1) {
            logger.log('LogVersionUpdates', componentName, `INFO: Component ${componentId} found version ${contextVersion} in context but not in global state`);
        }

        return contextVersion;
    }, [componentId, getComponentVersion]);

    const myCurrentVersionRef = useRef(getCurrentVersion());

    // Update refs when values change
    useEffect(() => {
        // Update refs when dependencies change
        onVersionChangeRef.current = onVersionChange;
        if (masterVersionRef.current !== masterVersion) {
            logger.log(`LogVersionUpdates`, componentName, `Updating masterVersionRef from ${masterVersionRef.current} to ${masterVersion}`);
            masterVersionRef.current = masterVersion;
        }
    }, [onVersionChange, masterVersion]);

    // Keep internal version reference updated
    useEffect(() => {
        myCurrentVersionRef.current = getCurrentVersion();
    }, [getCurrentVersion, masterVersion]);

    // Track loading state
    const hasLoadedRef = useRef(false);
    const isLoadingRef = useRef(false);
    const isInitializedRef = useRef(false);

    // Initialization logging
    useEffect(() => {
        if (!isInitializedRef.current) {
            logger.log(`LogVersionUpdates`, componentName,
                `Initializing version hook for ${componentId}, initialMasterVersion=${masterVersion}`);
            isInitializedRef.current = true;
        }

        // Cleanup
        return () => {
            logger.log(`LogVersionUpdates`, componentName,
                `Cleaning up version hook for ${componentId}, final version=${myCurrentVersionRef.current}`);
        };
    }, [componentId, masterVersion]);

    // Update component's version in global state
    const handleComponentVersionSet = useCallback((newVersion: number) => {
        // Skip if no change to prevent unnecessary updates
        if (myCurrentVersionRef.current === newVersion) {
            logger.log(`LogVersionUpdates`, componentName,
                `Skipping version update for ${componentId} - already at ${newVersion}`);
            return;
        }

        logger.log(`LogVersionUpdates`, componentName,
            `Setting component version for ${componentId} from ${myCurrentVersionRef.current} to ${newVersion}`);

        // Update both direct global and through context
        setVersionDirect(componentId, newVersion);
        setComponentVersion(componentId, newVersion);

        // Update our internal ref 
        myCurrentVersionRef.current = newVersion;

        debugGlobalState('After setting version');

        // Verify the version was set correctly by getting it again
        setTimeout(() => {
            const directVersion = getVersionDirect(componentId);
            const contextVersion = getComponentVersion(componentId);

            if (directVersion !== newVersion) {
                logger.log(`LogVersionUpdates`, componentName,
                    `WARNING: Direct global version mismatch after update for ${componentId}: expected ${newVersion}, got ${directVersion}`);
            }

            if (contextVersion !== newVersion) {
                logger.log(`LogVersionUpdates`, componentName,
                    `WARNING: Context version mismatch after update for ${componentId}: expected ${newVersion}, got ${contextVersion}`);
            }
        }, 0);
    }, [componentId, setComponentVersion, getComponentVersion]);

    // Request version update throttling
    const lastUpdateRequestTimeRef = useRef<number>(0);

    // Request master version update
    const requestVersionUpdate = useCallback(() => {
        const now = Date.now();
        const timeSinceLastRequest = now - lastUpdateRequestTimeRef.current;

        // Throttle requests to prevent rapid firing (optional, set to 100ms)
        if (timeSinceLastRequest < 100) {
            logger.log(`LogVersionUpdates`, componentName,
                `Throttling rapid version update request from ${componentId} - last request was ${timeSinceLastRequest}ms ago`);
            return;
        }

        // Update timestamp ref
        lastUpdateRequestTimeRef.current = now;

        // Get the most current master version - with fallback to direct global
        const currentMasterVersion = getMasterVersionDirect() || masterVersionRef.current;

        logger.log(`LogVersionUpdates`, componentName,
            `Requesting version update: component=${componentId}, currentMasterVersion=${currentMasterVersion}`);

        // Call the context update function
        updateMasterVersion({
            requestingComponent: componentId,
            currentVersion: currentMasterVersion
        });

        logger.log(`LogVersionUpdates`, componentName,
            `Version update request completed for ${componentId}`);
    }, [componentId, updateMasterVersion]);

    // Handle initial version check and sync with master
    useEffect(() => {
        if (!initialVersionCheckedRef.current) {
            // Get initial component version
            const initialVersion = getCurrentVersion();

            logger.log(`LogVersionUpdates`, componentName,
                `Initial version check for ${componentId}: masterVersion=${masterVersion}, componentVersion=${initialVersion}`);

            // If component version is -1 but master version is valid, sync them
            if (initialVersion === -1 && masterVersion >= 0) {
                logger.log(`LogVersionUpdates`, componentName,
                    `Initializing ${componentId} version to match master: ${masterVersion}`);

                // Update component version to match master on first initialization
                handleComponentVersionSet(masterVersion);

                // NOTE: Do NOT set hasLoadedRef.current = true here!
                // Let the data loading effect handle it after actually loading data.
                // Setting it here caused a race condition where components that mounted
                // after masterVersion was already set would never load their data.
            }

            // Track that initial check is done
            initialVersionCheckedRef.current = true;
        }
    }, [componentId, masterVersion, getCurrentVersion, handleComponentVersionSet]);

    // Handle data loading based on version changes
    useEffect(() => {
        // Prevent concurrent execution
        if (isLoadingRef.current) {
            logger.log(`LogVersionUpdates`, componentName,
                `Skipping effect for ${componentId} - already loading`);
            return;
        }

        // Get direct version from global state
        const directVersion = getVersionDirect(componentId);

        // Get version through context as backup
        const contextVersion = getComponentVersion(componentId);

        // Use the most reliable version (prefer direct global)
        const myVersion = directVersion !== -1 ? directVersion : contextVersion;

        // Update internal version ref
        myCurrentVersionRef.current = myVersion;

        // Log version check 
        logger.log(`LogVersionUpdates`, componentName,
            `Version check for ${componentId}: masterVersion=${masterVersion}, myVersion=${myVersion}, ` +
            `hasLoaded=${hasLoadedRef.current}, directVersion=${directVersion}, contextVersion=${contextVersion}`);

        // Determine if we should load data:
        // 1. Never loaded before OR
        // 2. Master version changed AND versions are valid AND not equal
        const shouldLoadData =
            !hasLoadedRef.current ||
            (masterVersion !== myVersion &&
                masterVersion >= 0 &&
                myVersion >= 0);

        if (shouldLoadData) {
            logger.log(`LogVersionUpdates`, componentName,
                `Will load data for ${componentId}: reason=${!hasLoadedRef.current ? 'initial load' : 'version changed'}`);

            // Set loading flag
            isLoadingRef.current = true;

            // Load the data
            const loadData = async () => {
                try {
                    logger.log(`LogVersionUpdates`, componentName,
                        `Starting data load for ${componentId} with version ${masterVersion}`);

                    if (onVersionChangeRef.current) {
                        // Call the latest onVersionChange function
                        await onVersionChangeRef.current(masterVersion);
                        logger.log(`LogVersionUpdates`, componentName,
                            `Data load callback completed for ${componentId}`);
                    } else {
                        logger.log(`LogVersionUpdates`, componentName,
                            `No onVersionChange callback for ${componentId}`);
                    }

                    // Mark as loaded and update our version
                    hasLoadedRef.current = true;
                    handleComponentVersionSet(masterVersion);

                    logger.log(`LogVersionUpdates`, componentName,
                        `Data load complete for ${componentId}, version set to ${masterVersion}`);
                } catch (error) {
                    // Log the error
                    console.error(`[${componentName}] Error loading data for ${componentId}:`, error);
                    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
                    logger.log(`LogVersionUpdates`, componentName,
                        `Error loading data for ${componentId}: ${errorMessage}`);

                    // Mark as loaded to prevent infinite retry loops
                    hasLoadedRef.current = true;
                } finally {
                    // Clear loading flag
                    isLoadingRef.current = false;
                }
            };

            loadData();
        }
    }, [masterVersion, getCurrentVersion, handleComponentVersionSet, componentId, getComponentVersion]);

    return {
        masterVersion,
        myCurrentVersion: myCurrentVersionRef.current,
        requestVersionUpdate,
        isLoading: isLoadingRef.current,
        hasLoaded: hasLoadedRef.current,
        // Convenience method to check if component is out of sync
        isOutOfSync: masterVersion !== myCurrentVersionRef.current && masterVersion >= 0 && myCurrentVersionRef.current >= 0
    };
};